/**
 * Portal voice assistant — the **Jarvis** widget (ADR-0025).
 *
 * The audible face of an app's `voice` interface: a launcher orb that opens a
 * full-screen HUD with a glowing reactive core. Tap to talk → the browser
 * transcribes (STT) → we stream the turn from `/voice/turn` (Claude tool-use
 * over the app's data under the user's RLS) → render + speak the answer (TTS).
 *
 * STT/TTS are browser-native (`lib/portalVoice`) — zero extra keys. If the
 * browser can't do speech, the launcher still opens and says so.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, Mic, MicOff, Send, X } from "lucide-react";

import {
  cancelSpeech,
  listenOnce,
  mediaRecorderSupported,
  primeSpeech,
  speak,
  speakViaServer,
  speechSupported,
  startRecording,
  streamVoiceTurn,
  transcribeViaServer,
  type Recording,
  type VoiceEvent,
} from "@/lib/portalVoice";
import { portalApi } from "@/lib/api-portal";
import { startMicAnalyser, type MicAnalyser } from "@/lib/audioLevel";
import { VoiceOrb } from "./VoiceOrb";
import { VoiceToolStage, type ToolActivity } from "./VoiceToolCards";
import {
  BootIn,
  Equalizer,
  HUD_KEYFRAMES,
  HudBackdrop,
  HudFrame,
  HudPanel,
  Radar,
  TeleRow,
} from "./VoiceHud";
import { useWakeWord } from "@/lib/useWakeWord";

type Phase = "idle" | "listening" | "thinking" | "speaking";
type Edge = "server" | "browser";

const CYAN = "#22d3ee";

// End-of-speech detection (VAD) for the server-STT push-to-talk path, so the
// turn auto-sends when you stop talking — a conversation, not a walkie-talkie.
// Calibrated against audioLevel's gained RMS (voice ≈ 0.15–0.6, silence ≈ 0.03–0.08).
const VAD_SPEECH_ON = 0.12; // level above this = voice present
const VAD_SILENCE_MS = 1000; // trailing silence after speech → send
const VAD_NO_SPEECH_MS = 7000; // never heard speech → stop, go idle (loop ends)
const VAD_MAX_MS = 20000; // hard cap on a single utterance

export function VoiceAssistant({
  appSlug,
  appName,
  sttMode = "browser",
  ttsMode = "browser",
  wakeWord = false,
  standalone = false,
  onClose,
  onExpand,
  switcher,
}: {
  appSlug: string;
  appName?: string;
  /** Which speech edges to use (ADR-0025), from portal branding. */
  sttMode?: Edge;
  ttsMode?: Edge;
  /** Run the in-browser "hey jarvis" wake word for hands-free open (ADR-0025). */
  wakeWord?: boolean;
  /** ADR-0049 Slice D — render as an AUTONOMOUS full-page surface (Jarvis-style):
   *  no orb, the HUD opens straight away (the host page IS the surface). */
  standalone?: boolean;
  /** What the HUD close (✕) does in standalone — the host passes a navigate-back. */
  onClose?: () => void;
  /** When set (docked widget only), shows an "expand" control in the HUD that
   *  opens the autonomous full-page voice surface (ADR-0049 Slice D). */
  onExpand?: () => void;
  /** Standalone-only: an interface switcher (Web ⇄ Chat ⇄ Voce) rendered in the
   *  HUD's top bar, so the user can hop between the app's front doors (ADR-0049). */
  switcher?: React.ReactNode;
}) {
  const { t } = useTranslation();
  // Standalone surfaces boot with the HUD already up (there's no orb to tap).
  const [open, setOpen] = React.useState(standalone);
  const [phase, setPhase] = React.useState<Phase>("idle");
  const [userText, setUserText] = React.useState("");
  const [answer, setAnswer] = React.useState("");
  const [toolNote, setToolNote] = React.useState("");
  const [errorNote, setErrorNote] = React.useState("");
  const [typed, setTyped] = React.useState("");
  const [toolActivity, setToolActivity] = React.useState<ToolActivity[]>([]);
  const toolSeqRef = React.useRef(0);

  // Server STT records audio for Whisper; browser STT uses Web Speech. Server
  // TTS plays Piper audio; browser TTS uses speechSynthesis.
  const serverStt = sttMode === "server" && mediaRecorderSupported();
  const serverTts = ttsMode === "server";
  // Can we listen at all? Server STT needs MediaRecorder (a SECURE context —
  // plain-http over LAN kills navigator.mediaDevices, the mobile case); when
  // it's unavailable fall back to browser Web Speech instead of declaring voice
  // unsupported (startListening already branches on serverStt). The typed
  // fallback always works regardless.
  const supported = serverStt || speechSupported();
  const insecure = typeof window !== "undefined" && window.isSecureContext === false;

  // Map a Web Speech error code to a human hint (desktop fails silently otherwise).
  const sttErrorMessage = React.useCallback(
    (reason?: string) => {
      if (reason === "not-allowed" || reason === "service-not-allowed")
        return t("voice.errMicBlocked", "Microphone blocked — allow it in the browser and retry.");
      if (reason === "no-speech") return t("voice.errNoSpeech", "I didn't catch that — tap and try again.");
      if (reason === "audio-capture")
        return t("voice.errNoMic", "No microphone found.");
      if (reason === "network")
        return t("voice.errNetwork", "Speech service unreachable — check the connection.");
      // Surface the raw code so an unmapped failure is diagnosable, not a shrug.
      const base = t("voice.error", "Sorry, something went wrong.");
      return reason ? `${base} (${reason})` : base;
    },
    [t],
  );

  const historyRef = React.useRef<{ role: "user" | "assistant"; text: string }[]>([]);
  // ADR-0028: the persisted thread this voice conversation is bound to (lazy; created
  // on the first turn). Voice stays ephemeral in the UI, but the turns are saved
  // server-side (within-session memory + audit). Reset when the HUD closes.
  const threadIdRef = React.useRef<string | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const stopListenRef = React.useRef<() => void>(() => {});
  const recordingRef = React.useRef<Recording | null>(null);
  // Live mic amplitude (0..1) the orb reads each frame; pumped while listening.
  const levelRef = React.useRef(0);
  const micRef = React.useRef<MicAnalyser | null>(null);
  const levelRafRef = React.useRef<number | null>(null);
  // Latest-value refs so async callbacks (TTS onEnd, the VAD loop) read current
  // state without re-creating the turn/detector each render.
  const openRef = React.useRef(false);
  const voiceTurnRef = React.useRef(false); // was the in-flight turn started by voice?
  const startListeningRef = React.useRef<() => void>(() => {});

  const stopMic = React.useCallback(() => {
    if (levelRafRef.current !== null) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
    micRef.current?.stop();
    micRef.current = null;
    levelRef.current = 0;
  }, []);

  const reset = React.useCallback(() => {
    abortRef.current?.abort();
    stopListenRef.current();
    recordingRef.current?.stop().catch(() => {});
    recordingRef.current = null;
    stopMic();
    cancelSpeech();
    setPhase("idle");
  }, [stopMic]);

  const openHud = () => {
    // The HUD-open tap is a user gesture: unlock TTS here (idempotent), well
    // away from recognition.start(), so the answer can be spoken later.
    primeSpeech();
    setOpen(true);
  };

  const close = () => {
    reset();
    openRef.current = false; // stop any in-flight TTS-onEnd from re-listening
    voiceTurnRef.current = false;
    setOpen(false);
    setUserText("");
    setAnswer("");
    setToolNote("");
    setErrorNote("");
    setTyped("");
    setToolActivity([]);
    historyRef.current = [];
    threadIdRef.current = null; // a reopened HUD starts a fresh voice thread
  };

  // Typed fallback — works even when browser STT is unreachable (Chrome
  // desktop `network` error). The answer is still spoken aloud (TTS).
  const submitTyped = (e: React.FormEvent) => {
    e.preventDefault();
    const text = typed.trim();
    if (!text || phase !== "idle") return;
    primeSpeech(); // this submit is a user gesture → unlock TTS for the answer
    voiceTurnRef.current = false; // typed turn → don't auto-listen after the answer
    setUserText(text);
    setTyped("");
    void runTurn(text);
  };

  // Clean up speech/stream if the widget unmounts mid-turn.
  React.useEffect(() => () => reset(), [reset]);

  // Human-like turn-taking: after the assistant finishes speaking, if the HUD is
  // still open and this was a VOICE turn, listen again for the follow-up — a real
  // back-and-forth, not one-shot. A voice turn that hears only silence (the VAD
  // no-speech timeout) ends the loop by going idle.
  const continueOrIdle = React.useCallback(() => {
    if (openRef.current && voiceTurnRef.current) startListeningRef.current();
    else setPhase("idle");
  }, []);

  const runTurn = React.useCallback(
    async (text: string) => {
      setPhase("thinking");
      setToolNote("");
      setAnswer("");
      setToolActivity([]); // fresh animations per turn
      historyRef.current.push({ role: "user", text });
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      let acc = "";
      // Bind (or lazily create) the persisted voice thread so the turn is saved
      // server-side. A create failure degrades to a stateless turn.
      if (!threadIdRef.current) {
        try {
          threadIdRef.current = (await portalApi.createAssistantSession(appSlug, "voice")).id;
        } catch {
          threadIdRef.current = null;
        }
      }
      await streamVoiceTurn(
        appSlug,
        {
          text,
          history: historyRef.current.slice(0, -1),
          thread_id: threadIdRef.current ?? undefined,
        },
        (ev: VoiceEvent) => {
          if (ev.type === "delta") {
            acc += ev.text;
            setAnswer(acc);
          } else if (ev.type === "tool") {
            setToolNote(t("voice.tool", "Looking that up…"));
          } else if (ev.type === "tool_result") {
            // Push an animated tool card (name + params + result preview).
            const id = `t${(toolSeqRef.current += 1)}`;
            setToolActivity((cur) => [...cur, { id, name: ev.name, preview: ev.preview }]);
          } else if (ev.type === "final") {
            acc = ev.text || acc;
            setAnswer(acc);
          } else if (ev.type === "error") {
            // Surface the backend code (e.g. http_404 / http_500) for diagnosis.
            setErrorNote(`${t("voice.error", "Sorry, something went wrong.")} (${ev.message})`);
          }
        },
        ctrl.signal,
      );
      historyRef.current.push({ role: "assistant", text: acc });
      setToolNote("");
      if (acc.trim()) {
        setPhase("speaking");
        // Server TTS (Piper) when configured — natural voice, same everywhere;
        // else the browser's speechSynthesis.
        // After speaking, continue the conversation (listen again) or go idle.
        if (serverTts) {
          void speakViaServer(appSlug, acc, { onEnd: continueOrIdle });
        } else {
          speak(acc, { onEnd: continueOrIdle });
        }
      } else {
        setPhase("idle"); // nothing to say (e.g. the turn errored) — loop ends
      }
    },
    [appSlug, t, serverTts, continueOrIdle],
  );

  // Finalise a server-STT push-to-talk capture: stop the recorder, transcribe
  // via the sidecar (Whisper), then run the turn. The reliable desktop path.
  const finishServerCapture = React.useCallback(async () => {
    const rec = recordingRef.current;
    recordingRef.current = null;
    stopMic();
    if (!rec) {
      setPhase("idle");
      return;
    }
    setPhase("thinking");
    try {
      const blob = await rec.stop();
      const text = (await transcribeViaServer(appSlug, blob)).trim();
      if (text) {
        setUserText(text);
        await runTurn(text);
      } else {
        // Silence (VAD heard nothing) → just wait, don't scold. The conversation
        // pauses; tap the orb or say "hey jarvis" to resume.
        setPhase("idle");
      }
    } catch {
      setErrorNote(t("voice.error", "Sorry, something went wrong."));
      setPhase("idle");
    }
  }, [appSlug, runTurn, stopMic, t]);

  const startListening = () => {
    // NOTE: TTS is unlocked on HUD-open (see launcher onClick), NOT here —
    // on desktop Chrome, calling synth.speak() right before recognition.start()
    // aborts the recognizer, collapsing the listen immediately.
    cancelSpeech();
    voiceTurnRef.current = true; // a voice-initiated turn → eligible for auto-continue
    setUserText("");
    setAnswer("");
    setErrorNote("");
    setPhase("listening");

    // Open a parallel mic analyser to drive the orb with real amplitude AND, on
    // the server-STT push-to-talk path, to detect end-of-speech (VAD) so the
    // turn auto-sends when the user stops — no tap. Browser STT auto-ends itself.
    let hasSpoken = false;
    let lastVoiceTs = performance.now();
    const vadStart = performance.now();
    void startMicAnalyser().then((mic) => {
      if (!mic) return; // denied/absent → orb falls back to synthetic motion
      micRef.current = mic;
      const pump = () => {
        if (!micRef.current) return;
        const lvl = micRef.current.level();
        levelRef.current = lvl;
        if (serverStt && recordingRef.current) {
          const now = performance.now();
          if (lvl > VAD_SPEECH_ON) {
            hasSpoken = true;
            lastVoiceTs = now;
          }
          const endOfSpeech = hasSpoken && now - lastVoiceTs > VAD_SILENCE_MS;
          const gaveUp = !hasSpoken && now - vadStart > VAD_NO_SPEECH_MS;
          const tooLong = now - vadStart > VAD_MAX_MS;
          if (endOfSpeech || gaveUp || tooLong) {
            // finishServerCapture nulls recordingRef + stops the mic → no re-fire.
            void finishServerCapture();
            return;
          }
        }
        levelRafRef.current = requestAnimationFrame(pump);
      };
      levelRafRef.current = requestAnimationFrame(pump);
    });

    if (serverStt) {
      // Server STT: push-to-talk. Record now; finalise on the next tap.
      void startRecording()
        .then((rec) => {
          recordingRef.current = rec;
        })
        .catch(() => {
          stopMic();
          setErrorNote(sttErrorMessage("not-allowed"));
          setPhase("idle");
        });
      return;
    }

    stopListenRef.current = listenOnce({
      onPartial: (txt) => setUserText(txt),
      onResult: (txt) => setUserText(txt),
      onError: (reason) => {
        stopMic();
        setErrorNote(sttErrorMessage(reason));
        setPhase("idle");
      },
      onEnd: () => {
        stopMic();
        // onResult already captured the text; kick the turn if we got something.
        setUserText((current) => {
          if (current.trim()) void runTurn(current.trim());
          else setPhase("idle");
          return current;
        });
      },
    }).stop;
  };

  // Keep the latest-value refs in sync for async callbacks (TTS onEnd / VAD).
  startListeningRef.current = startListening;
  openRef.current = open;

  const onCoreTap = () => {
    if (!supported) return;
    // The core tap IS a user gesture → unlock TTS here (idempotent). On the
    // standalone surface (ADR-0049 Slice D) the HUD boots open, so `openHud`'s
    // prime never runs; without this the first voice answer is muted by the
    // browser autoplay policy.
    primeSpeech();
    if (phase === "listening") {
      if (serverStt) void finishServerCapture();
      else stopListenRef.current();
    } else if (phase === "idle") {
      startListening();
    } else {
      reset(); // interrupt a thinking/speaking turn
    }
  };

  // Hands-free (ADR-0025): while the wake word is enabled and the HUD is
  // closed, listen for "hey jarvis" in the background; on fire, open the HUD
  // and start a listen turn. Paused (mic released) while the HUD is open so
  // the assistant's own STT owns the mic. Needs a usable mic (supported +
  // secure context).
  useWakeWord({
    active: wakeWord && !open && supported && !insecure,
    onWake: () => {
      openHud();
      // Give the HUD a tick to mount + the wake mic to release before the
      // assistant grabs the mic for its own capture.
      window.setTimeout(() => startListening(), 400);
    },
  });

  const phaseLabel =
    phase === "listening"
      ? t("voice.listening", "Listening…")
      : phase === "thinking"
        ? toolNote || t("voice.thinking", "Thinking…")
        : phase === "speaking"
          ? t("voice.speaking", "Speaking…")
          : supported
            ? t("voice.tap", "Tap to talk")
            : insecure
              ? t("voice.errInsecure", "Voice needs HTTPS — open the portal from a secure address.")
              : t("voice.unsupported", "Voice isn't supported in this browser.");

  return (
    <>
      {/* Launcher orb — bottom-right, always available where voice is on.
          Suppressed on the standalone surface (the HUD boots up directly). */}
      {!standalone && (
        <button
          type="button"
          aria-label={t("voice.open", "Voice assistant")}
          onClick={openHud}
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition hover:scale-105"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${CYAN}, #1e3a8a)`,
            boxShadow: `0 0 24px ${CYAN}66`,
          }}
        >
          <span
            className="absolute inset-0 rounded-full"
            style={{ boxShadow: `0 0 0 0 ${CYAN}55`, animation: "ping 2.5s cubic-bezier(0,0,0.2,1) infinite" }}
          />
          <Mic className="h-6 w-6" strokeWidth={1.75} />
          {/* Always-listening indicator: a live green dot when the wake word runs
              in the background (HUD closed). */}
          {wakeWord && !open && supported && !insecure && (
            <span
              className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 animate-pulse rounded-full border-2 border-[#0a1322] bg-emerald-400"
              style={{ boxShadow: "0 0 8px #34d399" }}
              title={t("voice.wakeHint", 'Always listening — say "hey jarvis"')}
            />
          )}
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[100] overflow-hidden"
          style={{ background: "radial-gradient(circle at 50% 40%, #0a1322f2, #000000fa)", backdropFilter: "blur(10px)" }}
        >
          <style>{HUD_KEYFRAMES}</style>
          <HudBackdrop />
          <HudFrame />

          {/* Interface switcher (standalone only) — hop to Web app / Chat. Styled
              to sit on the dark HUD; the switcher's own popover handles the menu. */}
          {standalone && switcher && (
            <div className="absolute left-7 top-7 z-20 text-cyan-100/90 [&_button:hover]:bg-cyan-400/10 [&_button]:text-cyan-100/90">
              {switcher}
            </div>
          )}

          {/* expand → full-page voice surface (docked widget only, ADR-0049 D) */}
          {!standalone && onExpand && (
            <button
              type="button"
              aria-label={t("voice.expand", "Apri a tutta pagina")}
              onClick={onExpand}
              className="absolute right-20 top-7 z-20 flex h-9 w-9 items-center justify-center rounded-md border border-cyan-400/30 text-cyan-100/70 transition hover:border-cyan-300/70 hover:text-white"
              style={{ background: "rgba(6,12,24,0.6)" }}
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          {/* close — HUD-styled */}
          <button
            type="button"
            aria-label={t("voice.close", "Close")}
            onClick={() => {
              close();
              onClose?.(); // standalone: leave the surface (host navigates back)
            }}
            className="absolute right-7 top-7 z-20 flex h-9 w-9 items-center justify-center rounded-md border border-cyan-400/30 text-cyan-100/70 transition hover:border-cyan-300/70 hover:text-white"
            style={{ background: "rgba(6,12,24,0.6)" }}
          >
            <X className="h-4 w-4" />
          </button>

          {/* left telemetry — audio edges + live equalizer */}
          <HudPanel title={t("voice.hudAudio", "Audio")} side="left" delay={0.45}>
            <TeleRow label="STT" value={serverStt ? "SERVER" : "BROWSER"} />
            <TeleRow label="TTS" value={serverTts ? "SERVER" : "BROWSER"} />
            {wakeWord && <TeleRow label="WAKE" value="HEY JARVIS" />}
            <div className="mt-1 flex justify-center">
              <Equalizer active={phase === "listening" || phase === "speaking"} />
            </div>
          </HudPanel>

          {/* right telemetry — session phase + tools + radar */}
          <HudPanel title={t("voice.hudSession", "Sessione")} side="right" delay={0.55}>
            <TeleRow label={t("voice.hudPhase", "Fase")} value={phase.toUpperCase()} />
            <TeleRow label={t("voice.hudTools", "Strumenti")} value={toolActivity.length} />
            <div className="mt-1 flex justify-center">
              <Radar />
            </div>
          </HudPanel>

          {/* main column */}
          <div className="relative z-10 flex h-full flex-col items-center justify-center gap-6 px-6 text-center">
            <BootIn delay={0.05}>
              <div className="text-sm font-semibold uppercase tracking-[0.45em] text-cyan-100/90" style={{ textShadow: `0 0 16px ${CYAN}66` }}>
                {appName || t("voice.title", "Assistant")}
              </div>
              <div className="mt-1.5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cyan-300/50">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" style={{ animation: "hudBlink 1.8s ease-in-out infinite", boxShadow: "0 0 8px #34d399" }} />
                ONLINE
              </div>
            </BootIn>

            {/* The reactive core — audio-reactive canvas HUD. The tool projections
                (Iron-Man edges + nodes) overlay it, emanating from the centre. */}
            <BootIn delay={0.15} className="relative flex items-center justify-center">
              <button
                type="button"
                onClick={onCoreTap}
                aria-label={t("voice.coreLabel", "Talk")}
                className="relative flex items-center justify-center outline-none transition-transform hover:scale-[1.02]"
              >
                <VoiceOrb phase={phase} levelRef={levelRef} size={300} />
                {!supported && (
                  <span className="absolute flex h-20 w-20 items-center justify-center rounded-full bg-black/40">
                    <MicOff className="h-9 w-9 text-white/80" strokeWidth={1.75} />
                  </span>
                )}
              </button>
              <VoiceToolStage items={toolActivity} />
            </BootIn>

            {/* phase chip — targeting style */}
            <BootIn delay={0.25}>
              <div
                className="inline-flex min-h-[1.9rem] items-center gap-2 rounded-md border border-cyan-400/30 px-4 py-1 text-xs uppercase tracking-[0.25em] text-cyan-100/90"
                style={{
                  background: "rgba(6,12,24,0.6)",
                  animation: phase !== "idle" ? "hudChipPulse 1.6s ease-in-out infinite" : undefined,
                }}
              >
                <span className="text-cyan-400/60">‹</span>
                {phaseLabel}
                <span className="text-cyan-400/60">›</span>
              </div>
            </BootIn>

            {/* transmission captions */}
            <BootIn delay={0.3} className="w-full max-w-xl space-y-2 text-left">
              {userText && (
                <p className="border-l-2 border-cyan-400/25 pl-3 font-mono text-xs text-cyan-100/50">
                  <span className="opacity-60">{t("voice.you", "You")} ›</span> {userText}
                </p>
              )}
              {(answer || phase === "thinking") && (
                <p
                  className="border-l-2 pl-3 text-base leading-relaxed text-cyan-50"
                  style={{ borderColor: `${CYAN}99`, textShadow: `0 0 14px ${CYAN}22` }}
                >
                  {answer}
                  {(phase === "thinking" || phase === "speaking") && (
                    <span className="ml-0.5 inline-block w-2" style={{ color: CYAN, animation: "hudCaret 0.9s steps(1) infinite" }}>
                      ▍
                    </span>
                  )}
                </p>
              )}
              {errorNote && <p className="border-l-2 border-amber-400/50 pl-3 text-sm text-amber-300/80">{errorNote}</p>}
            </BootIn>

            {/* console input — typed fallback, always available */}
            <BootIn delay={0.35} className="w-full max-w-md">
              <form onSubmit={submitTyped} className="flex items-center gap-2">
                <div
                  className="flex flex-1 items-center gap-2 rounded-md border border-cyan-400/30 px-3 py-2 transition focus-within:border-cyan-300/70"
                  style={{ background: "rgba(6,12,24,0.6)", boxShadow: `0 0 18px ${CYAN}11` }}
                >
                  <span className="font-mono text-sm text-cyan-400/70">›</span>
                  <input
                    type="text"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    disabled={phase !== "idle"}
                    placeholder={t("voice.typePlaceholder", "…or type your question")}
                    className="flex-1 bg-transparent font-mono text-sm text-cyan-50 outline-none placeholder:text-cyan-200/30 disabled:opacity-40"
                  />
                </div>
                <button
                  type="submit"
                  disabled={phase !== "idle" || !typed.trim()}
                  aria-label={t("voice.send", "Send")}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-cyan-400/40 text-cyan-100 transition hover:border-cyan-300/80 hover:text-white disabled:opacity-40"
                  style={{ background: `linear-gradient(135deg, ${CYAN}33, #1e3a8a55)` }}
                >
                  <Send className="h-4 w-4" strokeWidth={2} />
                </button>
              </form>
            </BootIn>
          </div>
        </div>
      )}
    </>
  );
}
